import api from "../../../utils/api";
import type {
  AvailabilityResult,
  ChargeTender,
  MaintenanceAppointment,
  MaintenanceDueBuckets,
  MaintenanceRecord,
  MaintenanceServiceItem,
  MaintenanceServiceRate,
  MaintenanceStatus,
  PageMeta,
  ServiceCatalogItem,
  VehicleOption,
  Workshop,
  WorkshopScheduleConfig,
} from "../types";

// ---------------------------------------------------------------------------
// API client for the Mantenimiento a Vehículos mini-app. Talks to the
// torre-de-control-api `maintenance` module endpoints.
//
// Two response envelopes are in play:
//   • pageResponse  → { data: [...], meta }              (lists)
//   • response      → { data: { message, data } }        (single / actions)
// `unwrap` collapses both to the inner payload.
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unwrap(res: any) {
  const d = res?.data?.data;
  if (d && typeof d === "object" && !Array.isArray(d) && "data" in d) {
    return d.data;
  }
  return d;
}

export interface MaintenanceListParams {
  page?: number;
  limit?: number;
  type?: string;
  status?: string;
  vehicleId?: string;
}

export async function fetchMaintenanceList(
  params: MaintenanceListParams = {}
): Promise<{ items: MaintenanceRecord[]; meta: PageMeta }> {
  const res = await api.get("/maintenanceList", {
    params: { limit: 500, page: 1, ...params },
  });
  return { items: res.data?.data ?? [], meta: res.data?.meta ?? {} };
}

export async function fetchMaintenanceByVehicle(
  vehicleId: string
): Promise<MaintenanceRecord[]> {
  const res = await api.get("/maintenanceByVehicle", { params: { vehicleId } });
  return unwrap(res) ?? [];
}

export async function createMaintenance(
  payload: Partial<MaintenanceRecord>
): Promise<MaintenanceRecord> {
  const res = await api.post("/maintenance", payload);
  return unwrap(res);
}

export async function updateMaintenance(
  id: string,
  payload: Partial<MaintenanceRecord>
): Promise<MaintenanceRecord> {
  const res = await api.put("/maintenance", payload, { params: { id } });
  return unwrap(res);
}

export async function deleteMaintenance(id: string): Promise<void> {
  await api.delete("/maintenance", { params: { id } });
}

export type MaintenanceReportKind = "workOrder" | "history" | "cost";

export interface MaintenanceReportParams {
  kind?: MaintenanceReportKind;
  /** Maintenance record id — required for the work-order report. */
  id?: string;
  /** Scope history/cost reports to a single vehicle (omit for the fleet). */
  vehicleId?: string;
  /** Optional ISO date range (history/cost). */
  startDate?: string;
  endDate?: string;
  /** When set, the backend also emails the PDF to this address. */
  email?: string;
}

export interface MaintenanceReportResult {
  url: string;
  path: string;
  kind?: MaintenanceReportKind;
  emailed?: boolean;
}

// Advances a record through the seguimiento state machine. The backend rejects
// invalid transitions (422).
export async function transitionMaintenance(
  id: string,
  status: MaintenanceStatus,
  extra: { scheduledDate?: string } = {}
): Promise<MaintenanceRecord> {
  const res = await api.post(
    "/maintenanceTransition",
    { status, ...extra },
    { params: { id } }
  );
  return unwrap(res);
}

// Reminder buckets: overdue / upcoming (within `withinDays`) / pending.
export async function fetchMaintenanceDue(
  withinDays = 7,
  vehicleId?: string
): Promise<MaintenanceDueBuckets> {
  const res = await api.get("/maintenanceDue", {
    params: { withinDays, ...(vehicleId ? { vehicleId } : {}) },
  });
  return (
    unwrap(res) ?? {
      overdue: [],
      upcoming: [],
      pending: [],
      counts: { overdue: 0, upcoming: 0, pending: 0, open: 0 },
      withinDays,
    }
  );
}

// Returns a signed URL to the generated PDF report. Supports the three report
// kinds (work order / history / cost) and optional email delivery.
export async function generateMaintenanceReport(
  params: MaintenanceReportParams = {}
): Promise<MaintenanceReportResult> {
  const query: Record<string, string> = {};
  if (params.kind) query.kind = params.kind;
  if (params.id) query.id = params.id;
  if (params.vehicleId) query.vehicleId = params.vehicleId;
  if (params.startDate) query.startDate = params.startDate;
  if (params.endDate) query.endDate = params.endDate;
  if (params.email) query.email = params.email;
  const res = await api.get("/maintenanceReport", { params: query });
  return unwrap(res);
}

export interface ChargeMaintenanceOptions {
  /** Payment method: cash settles now; card/link return a Stripe URL. */
  tender?: ChargeTender;
  /** Cart lines (labor + parts) referencing POS products. */
  serviceItems?: MaintenanceServiceItem[];
  /** Optional flat tax rate override; defaults to each product's POS tax. */
  taxRate?: number;
  /** Return URLs for the Stripe Checkout (card) flow. */
  successUrl?: string;
  cancelUrl?: string;
}

export interface ChargeMaintenanceResult {
  sale: unknown;
  /** Present for card/link tenders — the Stripe payment URL. */
  url?: string;
  maintenanceId: string;
}

// Charges an external-workshop work order through the POS. Cash settles
// immediately (invoice + payment); card/link return a Stripe URL settled by the
// webhook. No-op for own-mechanic orders (rejected by the backend).
export async function chargeMaintenance(
  id: string,
  options: ChargeMaintenanceOptions = {}
): Promise<ChargeMaintenanceResult> {
  const res = await api.post(
    "/maintenanceCharge",
    {
      tender: options.tender ?? "cash",
      ...(options.serviceItems ? { serviceItems: options.serviceItems } : {}),
      ...(options.taxRate !== undefined ? { taxRate: options.taxRate } : {}),
      ...(options.successUrl ? { successUrl: options.successUrl } : {}),
      ...(options.cancelUrl ? { cancelUrl: options.cancelUrl } : {}),
    },
    { params: { id } }
  );
  return unwrap(res);
}

// --- Service catalog = POS products (issue #79) ----------------------------
// The catalog is the POS products catalog: there is no parallel catalog. We
// read/write services through the /pos/product endpoints, marking them with
// type "service" so they're shared with the POS administration.
const SERVICE_CATEGORY = "Servicios de taller";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapProduct(p: any): ServiceCatalogItem {
  return {
    id: p.id,
    name: p.name,
    description: p.description ?? null,
    price: Number(p.price ?? 0),
    currency: p.currency ?? "MXN",
    taxRate: p.taxRate !== undefined && p.taxRate !== null ? Number(p.taxRate) : 16,
    sku: p.sku ?? null,
    type: p.type ?? "service",
    active: p.status ? p.status === "active" : true,
  };
}

// Services only (type "service"): drives the catalog management screen.
export async function fetchServiceCatalog(): Promise<ServiceCatalogItem[]> {
  const res = await api.get("/pos/productsList", {
    params: { type: "service", limit: 500, page: 1 },
  });
  const items = res.data?.data ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return items.map((p: any) => mapProduct(p));
}

// Active POS products (services + parts) used to compose a work-order cart.
export async function fetchServiceProducts(): Promise<ServiceCatalogItem[]> {
  const res = await api.get("/pos/productsList", {
    params: { status: "active", limit: 500, page: 1 },
  });
  const items = res.data?.data ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return items.map((p: any) => mapProduct(p));
}

export async function createServiceCatalog(
  payload: Partial<ServiceCatalogItem>
): Promise<ServiceCatalogItem> {
  const res = await api.post("/pos/product", {
    name: payload.name,
    description: payload.description ?? null,
    price: Number(payload.price ?? 0),
    type: "service",
    category: SERVICE_CATEGORY,
    ...(payload.taxRate !== undefined ? { taxRate: Number(payload.taxRate) } : {}),
  });
  return mapProduct(unwrap(res));
}

export async function updateServiceCatalog(
  id: string,
  payload: Partial<ServiceCatalogItem>
): Promise<ServiceCatalogItem> {
  const res = await api.put(
    "/pos/product",
    {
      name: payload.name,
      description: payload.description ?? null,
      price: Number(payload.price ?? 0),
      type: "service",
      ...(payload.taxRate !== undefined ? { taxRate: Number(payload.taxRate) } : {}),
    },
    { params: { id } }
  );
  return mapProduct(unwrap(res));
}

export async function deleteServiceCatalog(id: string): Promise<void> {
  await api.delete("/pos/product", { params: { id } });
}

// --- Tabulador del taller (maintenance_services, sólo lectura) -------------
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapRate(s: any): MaintenanceServiceRate {
  return {
    id: s.id,
    name: s.name,
    description: s.description ?? null,
    category: s.category ?? null,
    price: Number(s.price ?? 0),
    currency: s.currency ?? "MXN",
    durationMinutes: Number(s.durationMinutes ?? 0),
    sku: s.sku ?? null,
    photo: s.photo ?? null,
    active: s.active !== false,
  };
}

export async function fetchServiceRates(): Promise<MaintenanceServiceRate[]> {
  const res = await api.get("/maintenanceServiceList");
  const list = unwrap(res) ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (Array.isArray(list) ? list : []).map((s: any) => mapRate(s));
}

// --- Workshop agenda / reservations (issue #80) ----------------------------
export async function fetchScheduleConfig(): Promise<WorkshopScheduleConfig> {
  const res = await api.get("/maintenanceScheduleConfig");
  return unwrap(res);
}

export async function updateScheduleConfig(
  payload: Partial<WorkshopScheduleConfig>
): Promise<WorkshopScheduleConfig> {
  const res = await api.put("/maintenanceScheduleConfig", payload);
  return unwrap(res);
}

export interface AppointmentListParams {
  date?: string; // YYYY-MM-DD
  vehicleId?: string;
  status?: string;
}

export async function fetchAppointments(
  params: AppointmentListParams = {}
): Promise<MaintenanceAppointment[]> {
  const res = await api.get("/maintenanceAppointmentList", { params });
  return unwrap(res) ?? [];
}

export async function fetchAvailability(params: {
  date: string;
  durationMinutes?: number;
  serviceId?: string;
}): Promise<AvailabilityResult> {
  const res = await api.get("/maintenanceAvailability", { params });
  return unwrap(res);
}

export async function createAppointment(
  payload: Partial<MaintenanceAppointment>
): Promise<MaintenanceAppointment> {
  const res = await api.post("/maintenanceAppointment", payload);
  return unwrap(res);
}

export async function updateAppointment(
  id: string,
  payload: Partial<MaintenanceAppointment>
): Promise<MaintenanceAppointment> {
  const res = await api.put("/maintenanceAppointment", payload, {
    params: { id },
  });
  return unwrap(res);
}

export async function deleteAppointment(id: string): Promise<void> {
  await api.delete("/maintenanceAppointment", { params: { id } });
}

// --- Workshops / mechanics (mode config, issue #78) ------------------------
export async function fetchWorkshops(): Promise<Workshop[]> {
  const res = await api.get("/maintenanceWorkshopList");
  return unwrap(res) ?? [];
}

export async function createWorkshop(
  payload: Partial<Workshop>
): Promise<Workshop> {
  const res = await api.post("/maintenanceWorkshop", payload);
  return unwrap(res);
}

export async function updateWorkshop(
  id: string,
  payload: Partial<Workshop>
): Promise<Workshop> {
  const res = await api.put("/maintenanceWorkshop", payload, { params: { id } });
  return unwrap(res);
}

export async function deleteWorkshop(id: string): Promise<void> {
  await api.delete("/maintenanceWorkshop", { params: { id } });
}

// --- Vehicles (selector) ---------------------------------------------------
export async function fetchVehicleOptions(): Promise<VehicleOption[]> {
  const res = await api.get("/vehicleList", { params: { limit: 500, page: 1 } });
  const items = res.data?.data ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return items.map((v: any) => ({
    id: v.id,
    label:
      v.name ||
      v.alias ||
      v.plate ||
      v.plates ||
      v.economicNumber ||
      v.economic_number ||
      v.id,
    image: typeof v.image === "string" && v.image ? v.image : null,
    plate: v.plate || v.plates || null,
    type: v.type ?? null,
    brand: v.brand ?? null,
    model: v.model ?? null,
    year: v.year ? Number(v.year) : null,
  }));
}
